import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usersAPI, mentorshipAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface Mentor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
  bio?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  expertise?: {
    technical: string[];
    mentoring: string[];
    industries: string[];
  };
  experience?: {
    yearsOfExperience: number;
    currentCompany?: string;
    currentRole?: string;
  };
  availability?: {
    timeSlots: string[];
  };
  mentoringApproach?: string;
  isApproved: boolean;
  createdAt: string;
}

interface MentorshipRequest {
  _id: string;
  mentor: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  message: string;
  status: 'pending' | 'accepted' | 'rejected' | 'disconnected';
  createdAt: string;
  respondedAt?: string;
  mentorNotes?: string;
}

const FindMentors: React.FC = () => {
  const { user } = useAuth();
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [filteredMentors, setFilteredMentors] = useState<Mentor[]>([]);
  const [mentorshipRequests, setMentorshipRequests] = useState<MentorshipRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [skillsFilter, setSkillsFilter] = useState('');
  
  // Get all unique skills for filter dropdown
  const [allSkills, setAllSkills] = useState<string[]>([]);
  
  // Mentorship request modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);
  const [message, setMessage] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState('');

  useEffect(() => {
    fetchMentors();
    if (user?.role === 'aspirant') {
      fetchMentorshipRequests();
    }
  }, [user]);

  useEffect(() => {
    filterMentors();
  }, [searchTerm, selectedSkills, mentors]);

  const fetchMentors = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getMentors();
      
      if (response.success && response.data) {
        setMentors(response.data);
        setFilteredMentors(response.data);
        
        // Extract all unique skills
        const skillsSet = new Set<string>();
        response.data.forEach((mentor: Mentor) => {
          mentor.expertise?.technical?.forEach(skill => skillsSet.add(skill));
          mentor.expertise?.mentoring?.forEach(skill => skillsSet.add(skill));
        });
        setAllSkills(Array.from(skillsSet).filter(skill => skill.trim() !== ''));
      } else {
        throw new Error(response.message || 'Failed to fetch mentors');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch mentors');
    } finally {
      setLoading(false);
    }
  };

  const fetchMentorshipRequests = async () => {
    try {
      const response = await mentorshipAPI.getAspirantRequests('all', 1, 100);
      if (response.success && response.data?.requests) {
        setMentorshipRequests(response.data.requests);
      }
    } catch (err: any) {
      console.error('Error fetching mentorship requests:', err);
    }
  };

  const filterMentors = () => {
    let filtered = mentors;

    // Filter by search term (name, bio, company, role)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(mentor =>
        mentor.firstName.toLowerCase().includes(term) ||
        mentor.lastName.toLowerCase().includes(term) ||
        mentor.bio?.toLowerCase().includes(term) ||
        mentor.experience?.currentCompany?.toLowerCase().includes(term) ||
        mentor.experience?.currentRole?.toLowerCase().includes(term) ||
        mentor.email.toLowerCase().includes(term)
      );
    }

    // Filter by selected skills
    if (selectedSkills.length > 0) {
      filtered = filtered.filter(mentor => {
        const mentorSkills = [
          ...(mentor.expertise?.technical || []),
          ...(mentor.expertise?.mentoring || [])
        ];
        return selectedSkills.every(skill => 
          mentorSkills.some(mentorSkill => 
            mentorSkill.toLowerCase().includes(skill.toLowerCase())
          )
        );
      });
    }

    setFilteredMentors(filtered);
  };

  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev => 
      prev.includes(skill) 
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSkills([]);
    setSkillsFilter('');
  };

  const handleConnectClick = (mentor: Mentor) => {
    setSelectedMentor(mentor);
    setMessage('');
    setError('');
    setRequestSuccess('');
    setIsModalOpen(true);
  };

  const handleSendRequest = async () => {
    if (!selectedMentor || !message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (message.trim().length < 10) {
      setError('Message must be at least 10 characters long');
      return;
    }

    try {
      setRequesting(true);
      setError('');
      
      await mentorshipAPI.sendRequest(selectedMentor._id, message.trim());
      
      // Refresh mentorship requests to update status badges
      await fetchMentorshipRequests();
      
      setRequestSuccess('Mentorship request sent successfully!');
      setTimeout(() => {
        setIsModalOpen(false);
        setSelectedMentor(null);
        setMessage('');
        setRequestSuccess('');
      }, 2000);
      
    } catch (err: any) {
      console.error('Error sending mentorship request:', err);
      setError(err.response?.data?.message || 'Failed to send mentorship request');
    } finally {
      setRequesting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMentor(null);
    setMessage('');
    setError('');
    setRequestSuccess('');
  };

  // Helper function to get mentorship status with a mentor
  const getMentorshipStatus = (mentorId: string): 'pending' | 'accepted' | null => {
    const request = mentorshipRequests.find(req => 
      req.mentor._id === mentorId && 
      (req.status === 'pending' || req.status === 'accepted')
    );
    return request ? (request.status === 'pending' ? 'pending' : 'accepted') : null;
  };

  if (loading) {
    return (
      <LoadingSpinner 
        text="Finding Mentors"
        subtitle="Connecting you with experienced professionals..."
        size="lg"
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-red-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <svg className="h-10 w-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-gray-800">Error Loading Mentors</h3>
            <p className="text-gray-600 leading-relaxed">{error}</p>
          </div>
          <button 
            onClick={fetchMentors}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <h1 className="text-5xl font-bold text-gray-900">Find Mentors</h1>
              <p className="text-gray-600 text-lg">Connect with experienced professionals to guide your career journey</p>
            </div>
            
            {/* Search and Filters */}
            <div className="space-y-4">
              {/* Search Bar and Skills Filter Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Search Bar */}
                <div className="relative lg:col-span-2">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search mentors by name, bio, company, role..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Skills Filter Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Filter by skills..."
                    value={skillsFilter}
                    onChange={(e) => setSkillsFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Clear Filters Button */}
              {(searchTerm || selectedSkills.length > 0) && (
                <div className="flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-purple-600 hover:text-purple-700 font-medium text-sm"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
              
              {/* Skills Tags */}
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {allSkills
                    .filter(skill => skill.toLowerCase().includes(skillsFilter.toLowerCase()))
                    .slice(0, 20)
                    .map((skill) => (
                    <button
                      key={skill}
                      onClick={() => handleSkillToggle(skill)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                        selectedSkills.includes(skill)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
                
                {/* Selected Skills */}
                {selectedSkills.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Selected Skills:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedSkills.map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-medium flex items-center"
                        >
                          {skill}
                          <button
                            onClick={() => handleSkillToggle(skill)}
                            className="ml-2 hover:bg-purple-700 rounded-full p-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Results Count */}
            <p className="text-gray-600">
              Found {filteredMentors.length} mentor{filteredMentors.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Mentors Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {filteredMentors.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredMentors.map((mentor) => {
              const mentorshipStatus = getMentorshipStatus(mentor._id);
              
              return (
                <div key={mentor._id} className="bg-white rounded-2xl shadow-lg border border-purple-200 overflow-hidden hover:shadow-xl transition-all duration-300 relative">
                  {/* Status Badge */}
                  {mentorshipStatus && (
                    <div className="absolute top-4 right-4 z-10">
                      <Badge 
                        className={`${
                          mentorshipStatus === 'accepted' 
                            ? 'bg-green-100 text-green-800 border-green-200' 
                            : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                        } font-medium shadow-sm`}
                      >
                        {mentorshipStatus === 'accepted' ? 'Connected' : 'Pending'}
                      </Badge>
                    </div>
                  )}
                  
                  {/* Profile Header */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 px-6 py-6">
                    <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
                      {mentor.profileImageUrl ? (
                        <img
                          src={mentor.profileImageUrl}
                          alt={`${mentor.firstName} ${mentor.lastName}`}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-xl font-bold">
                          {mentor.firstName[0]}{mentor.lastName[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">
                        {mentor.firstName} {mentor.lastName}
                      </h3>
                      {mentor.experience?.currentRole && (
                        <p className="text-purple-700 font-medium">
                          {mentor.experience.currentRole}
                        </p>
                      )}
                      {mentor.experience?.currentCompany && (
                        <p className="text-purple-600 text-sm">
                          {mentor.experience.currentCompany}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Profile Content */}
                <div className="p-6 space-y-4">
                  {/* Experience */}
                  {mentor.experience?.yearsOfExperience && (
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {mentor.experience.yearsOfExperience} years of experience
                    </div>
                  )}

                  {/* Bio */}
                  {mentor.bio && (
                    <p className="text-gray-700 text-sm line-clamp-3">
                      {mentor.bio}
                    </p>
                  )}

                  {/* Skills */}
                  {(() => {
                    const allSkills = [
                      ...(mentor.primarySkills || []),
                      ...(mentor.secondarySkills || []),
                      ...(mentor.expertise?.technical || []),
                      ...(mentor.expertise?.mentoring || []),
                      ...(mentor.expertise?.industries || [])
                    ].filter(skill => skill && skill.trim());
                    
                    return allSkills.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-900">Skills & Expertise</p>
                        <div className="flex flex-wrap gap-1">
                          {allSkills.slice(0, 6).map((skill, index) => (
                            <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                              {skill}
                            </span>
                          ))}
                          {allSkills.length > 6 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                              +{allSkills.length - 6} more
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Mentoring Approach */}
                  {mentor.mentoringApproach && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-900">Mentoring Approach</p>
                      <p className="text-gray-700 text-sm line-clamp-2">
                        {mentor.mentoringApproach}
                      </p>
                    </div>
                  )}

                  {/* Availability */}
                  {mentor.availability?.timeSlots && mentor.availability.timeSlots.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-900">Available Time Slots</p>
                      <div className="flex flex-wrap gap-1">
                        {mentor.availability.timeSlots.slice(0, 3).map((slot, index) => (
                          <span key={index} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                            {slot}
                          </span>
                        ))}
                        {mentor.availability.timeSlots.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                            +{mentor.availability.timeSlots.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="px-6 pb-6">
                  {mentorshipStatus ? (
                    <button 
                      disabled
                      className="w-full bg-gray-300 text-gray-500 px-4 py-3 rounded-xl font-semibold shadow-lg cursor-not-allowed"
                    >
                      {mentorshipStatus === 'accepted' ? 'Already Connected' : 'Request Pending'}
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleConnectClick(mentor)}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Connect with Mentor
                    </button>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <svg className="w-24 h-24 text-purple-300 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">No Mentors Found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedSkills.length > 0 
                ? "Try adjusting your search criteria or clearing filters" 
                : "No mentors are currently available"
              }
            </p>
            {(searchTerm || selectedSkills.length > 0) && (
              <button
                onClick={clearFilters}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mentorship Request Modal */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              Connect with {selectedMentor?.firstName} {selectedMentor?.lastName}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Send a personalized message to introduce yourself and explain why you'd like this mentorship.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                Your Message
              </Label>
              <Textarea
                id="message"
                placeholder="Hi! I'm interested in connecting with you as a mentor because..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[120px] resize-none"
                maxLength={1000}
              />
              <div className="text-xs text-gray-500 text-right">
                {message.length}/1000 characters (minimum 10 required)
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}
            
            {requestSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                {requestSuccess}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeModal}
              disabled={requesting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSendRequest}
              disabled={requesting || message.trim().length < 10}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {requesting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                'Send Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FindMentors; 